import React, { useState, useEffect } from "react";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Resize,
  Sort,
  ContextMenu,
  Filter,
  Page,
  ExcelExport,
  PdfExport,
  Inject,
} from "@syncfusion/ej2-react-grids";
import { Header } from "../Components";
import axios from "axios";
import {
  FaSearch,
  FaTrash,
  FaTimes,
  FaFileExcel,
  FaFilePdf,
  FaPlus,
  FaEdit,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";
// Grid column configuration
const galleryGrid = [
  {
    field: "id",
    headerText: "ID",
    width: 80,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">ID</span>,
  },
  {
    field: "name",
    headerText: "Name",
    width: 150,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Name</span>,
  },
  {
    field: "src",
    headerText: "Image",
    width: 100,
    textAlign: "Center",
    template: (props) => (
      <img
        src={props.src}
        alt="Gallery"
        className="w-12 h-12 object-cover rounded"
      />
    ),
    headerTemplate: () => <span className="font-bold">Image</span>,
  },
  {
    field: "Actions",
    headerText: "Actions",
    width: 120,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Actions</span>,
    template: (props) => (
      <div className="flex justify-center gap-3">
        <button
          className="text-blue-600 hover:text-blue-800 transition-transform transform hover:scale-110 focus:outline-none"
          onClick={() => props.editHandler(props)}
          title="Edit"
        >
          <FaEdit size={18} />
        </button>
        <button
          className="text-red-600 hover:text-red-800 transition-transform transform hover:scale-110 focus:outline-none"
          onClick={() => props.deleteHandler(props.id)}
          title="Delete"
        >
          <FaTrash size={18} />
        </button>
      </div>
    ),
  },
];

const Gallery = () => {
  const [galleryData, setGalleryData] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", image: null });

  const { baseUrl } = useBaseUrl();

  // Fetch gallery data from API
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const formData = new FormData();
      formData.append("action", "get-event-gallery");

      const response = await axios.post(`${baseUrl}gallery.php`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          // 'Accept': 'application/json',
        },
        withCredentials: true,
      });
      console.log("API Response:", response.data);
      if (response.data.status) {
        setGalleryData(response.data.data || []);
      } else {
        setErrorMessage(response.data.error || "Failed to fetch gallery.");
      }
    } catch (error) {
      console.error("Axios Error:", error.response || error);
      setErrorMessage(
        "Error connecting to the server. Please check your network or API configuration."
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle add gallery
  const handleAddGallery = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    try {
      const data = new FormData();
      data.append("action", "add-event-photo");
      data.append("title", formData.title);
      data.append("photo", formData.photo);

      const response = await axios.post(`${baseUrl}gallery.php`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          // 'Accept': 'application/json',
        },
        withCredentials: true,
      });
      console.log("Add Response:", response.data);
      if (response.data.status) {
        fetchData();
        // setGalleryData([...galleryData, response.data.data]);
        setIsAddModalOpen(false);
        setFormData({ name: "", image: null });
        alert("Gallery photo added successfully.");
      } else {
        setErrorMessage(response.data.error || "Failed to add gallery photo.");
      }
    } catch (error) {
      console.error("Error adding gallery:", error.response || error);
      setErrorMessage("Failed to add gallery photo.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit gallery
  const handleEditGallery = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    try {
      const data = new FormData();
      data.append("action", "update-event-photo");
      data.append("id", selectedGallery.id);
      data.append("title", formData.title);
      if (formData.photo) data.append("photo", formData.photo);

      const response = await axios.post(`${baseUrl}gallery.php`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
          // 'Accept': 'application/json',
        },
        withCredentials: true,
      });
      console.log("Edit Response:", response.data);
      if (response.data.status) {
        fetchData();
        // setGalleryData(galleryData.map(item => item.id === selectedGallery.id ? response.data.data : item));
        setIsEditModalOpen(false);
        setFormData({ title: "", image: null });
        setSelectedGallery(null);
        alert("Gallery photo updated successfully.");
      } else {
        setErrorMessage(
          response.data.error || "Failed to update gallery photo."
        );
      }
    } catch (error) {
      console.error("Error updating gallery:", error.response || error);
      setErrorMessage("Failed to update gallery photo.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete action
  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this gallery photo?")) {
      try {
        const data = new FormData();
        data.append("action", "delete-event-photo");
        data.append("id", id);

        const response = await axios.post(`${baseUrl}gallery.php`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            // 'Accept': 'application/json',
          },
          withCredentials: true,
        });
        console.log("Delete Response:", response.data);
        if (response.data.status) {
          setGalleryData(galleryData.filter((item) => item.id !== id));
          alert("Gallery photo deleted successfully.");
        } else {
          alert(
            "Failed to delete gallery photo: " +
              (response.data.error || "Unknown error")
          );
        }
      } catch (error) {
        console.error("Error deleting gallery:", error.response || error);
        alert("Failed to delete gallery photo.");
      }
    }
  };

  // Handle edit action
  const editHandler = (gallery) => {
    setSelectedGallery(gallery);
    setFormData({
      name: gallery.name,
      image: null,
    });
    setIsEditModalOpen(true);
  };

  // Search filter
  const filteredData = galleryData.filter((item) =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Inject handlers into grid data
  const gridData = filteredData.map((item) => ({
    ...item,
    editHandler,
    deleteHandler,
  }));

  const { setCurrentColor, setCurrentMode, currentMode } = useStateContext();

  useEffect(() => {
    const currentThemeColor = localStorage.getItem("colorMode");
    const currentThemeMode = localStorage.getItem("themeMode");
    if (currentThemeColor && currentThemeMode) {
      setCurrentColor(currentThemeColor);
      setCurrentMode(currentThemeMode);
    }
  }, []);

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fadeIn bg-gray-100 min-h-screen"
    >
      <Header category="Dashboard" title="Gallery Management" />

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          {errorMessage}
        </div>
      )}

      {/* Search and Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 bg-white shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-300 shadow-md"
            onClick={() => {
              setFormData({ title: "", image: null });
              setIsAddModalOpen(true);
            }}
          >
            <FaPlus className="mr-2" /> Add Photo
          </button>
          <button
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() =>
              document
                .getElementById("galleryGrid")
                .ej2_instances[0].excelExport()
            }
            disabled={isLoading}
          >
            <FaFileExcel className="mr-2" /> Export Excel
          </button>
          <button
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() =>
              document
                .getElementById("galleryGrid")
                .ej2_instances[0].pdfExport()
            }
            disabled={isLoading}
          >
            <FaFilePdf className="mr-2" /> Export PDF
          </button>
        </div>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Grid */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <GridComponent
          id="galleryGrid"
          dataSource={gridData}
          allowPaging={true}
          allowSorting={true}
          allowExcelExport={true}
          allowPdfExport={true}
          allowFiltering={true}
          allowResizing={true}
          pageSettings={{ pageSize: 10, pageSizes: [10, 20, 50] }}
          filterSettings={{ type: "Menu", showFilterBarStatus: true }}
        >
          <ColumnsDirective>
            {galleryGrid.map((item, index) => (
              <ColumnDirective key={index} {...item} />
            ))}
          </ColumnsDirective>
          <Inject
            services={[
              Resize,
              Sort,
              ContextMenu,
              Filter,
              Page,
              ExcelExport,
              PdfExport,
            ]}
          />
        </GridComponent>
      </div>

      {/* Add Gallery Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Add Gallery Photo
              </h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition"
                onClick={() => setIsAddModalOpen(false)}
              >
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleAddGallery} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  onChange={(e) =>
                    setFormData({ ...formData, photo: e.target.files[0] })
                  }
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  disabled={isLoading}
                >
                  Add Photo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Gallery Modal */}
      {isEditModalOpen && selectedGallery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Edit Gallery Photo
              </h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition"
                onClick={() => setIsEditModalOpen(false)}
              >
                <FaTimes size={20} />
              </button>
            </div>
            <form onSubmit={handleEditGallery} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Image (optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  onChange={(e) =>
                    setFormData({ ...formData, photo: e.target.files[0] })
                  }
                />
                {selectedGallery.src && (
                  <img
                    src={selectedGallery.src}
                    alt="Current"
                    className="mt-2 w-24 h-24 object-cover rounded"
                  />
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                  disabled={isLoading}
                >
                  Update Photo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
